import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, Code, Save, X, Info, Copy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import RichTextEditor from './RichTextEditor';

import { supabase } from "@/integrations/supabase/client";
import { notify } from "@/utils/notification";
import { createSafeHTML } from "@/utils/sanitization";
import { useToast } from '@/hooks/use-toast';

interface EmailTemplate {
  id?: string;
  name: string;
  subject: string;
  template_type: string;
  content_html: string;
  content_text: string;
  is_active?: boolean;
  metadata?: any;
}

interface EmailTemplateFormProps {
  template?: EmailTemplate | null;
  onSave: (template: EmailTemplate) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// Template type configurations with available variables
const templateTypeConfig: Record<string, { 
  label: string; 
  category: 'system' | 'marketing';
  variables: { name: string; description: string }[];
}> = {
  booking_confirmed_by_practitioner: {
    label: 'Booking Confirmed by Practitioner',
    category: 'system',
    variables: [
      { name: 'client_name', description: 'Client full name' },
      { name: 'practitioner_name', description: 'Practitioner full name' },
      { name: 'service_name', description: 'Service name' },
      { name: 'booking_datetime', description: 'Booking date and time' },
      { name: 'format', description: 'Session format (Online/In-Person)' },
      { name: 'location_details', description: 'Location details (if in-person)' },
      { name: 'dashboard_url', description: 'Dashboard link' },
    ],
  },
  booking_declined_by_practitioner: {
    label: 'Booking Declined by Practitioner',
    category: 'system',
    variables: [
      { name: 'client_name', description: 'Client full name' },
      { name: 'practitioner_name', description: 'Practitioner full name' },
      { name: 'service_name', description: 'Service name' },
      { name: 'booking_datetime', description: 'Original booking date' },
      { name: 'decline_reason', description: 'Reason for declining' },
      { name: 'explore_url', description: 'Explore practitioners link' },
    ],
  },
  booking_pending_confirmation_client: {
    label: 'Booking Pending (Client)',
    category: 'system',
    variables: [
      { name: 'client_name', description: 'Client full name' },
      { name: 'practitioner_name', description: 'Practitioner full name' },
      { name: 'service_name', description: 'Service name' },
      { name: 'booking_datetime', description: 'Booking date and time' },
      { name: 'format', description: 'Session format' },
      { name: 'currency', description: 'Currency code' },
      { name: 'price', description: 'Price amount' },
      { name: 'dashboard_url', description: 'Dashboard link' },
    ],
  },
  booking_pending_confirmation_practitioner: {
    label: 'New Booking Request (Practitioner)',
    category: 'system',
    variables: [
      { name: 'practitioner_name', description: 'Practitioner full name' },
      { name: 'client_name', description: 'Client full name' },
      { name: 'service_name', description: 'Service name' },
      { name: 'booking_datetime', description: 'Booking date and time' },
      { name: 'format', description: 'Session format' },
      { name: 'currency', description: 'Currency code' },
      { name: 'price', description: 'Price amount' },
      { name: 'client_notes', description: 'Notes from client' },
      { name: 'confirmation_deadline', description: 'Deadline to confirm' },
      { name: 'dashboard_url', description: 'Dashboard link' },
    ],
  },
  payment_captured_client: {
    label: 'Payment Completed (Client)',
    category: 'system',
    variables: [
      { name: 'client_name', description: 'Client full name' },
      { name: 'practitioner_name', description: 'Practitioner full name' },
      { name: 'service_name', description: 'Service name' },
      { name: 'booking_datetime', description: 'Session date' },
      { name: 'currency', description: 'Currency code' },
      { name: 'price', description: 'Amount charged' },
    ],
  },
  payment_captured_practitioner: {
    label: 'Payment Received (Practitioner)',
    category: 'system',
    variables: [
      { name: 'practitioner_name', description: 'Practitioner full name' },
      { name: 'client_name', description: 'Client full name' },
      { name: 'service_name', description: 'Service name' },
      { name: 'booking_datetime', description: 'Session date' },
      { name: 'currency', description: 'Currency code' },
      { name: 'price', description: 'Amount received' },
    ],
  },
  confirmation_reminder: {
    label: 'Confirmation Reminder',
    category: 'system',
    variables: [
      { name: 'practitioner_name', description: 'Practitioner full name' },
      { name: 'client_name', description: 'Client full name' },
      { name: 'service_name', description: 'Service name' },
      { name: 'booking_datetime', description: 'Booking date and time' },
      { name: 'hours_remaining', description: 'Hours left to confirm' },
      { name: 'dashboard_url', description: 'Dashboard link' },
    ],
  },
  chat_message: {
    label: 'Chat Message Notification',
    category: 'system',
    variables: [
      { name: 'sender_name', description: 'Message sender name' },
      { name: 'recipient_name', description: 'Message recipient name' },
      { name: 'service_name', description: 'Related service name' },
      { name: 'message_content', description: 'Message text' },
      { name: 'chat_url', description: 'Conversation link' },
    ],
  },
  welcome: { label: 'Welcome Email', category: 'marketing', variables: [{ name: 'first_name', description: 'User first name' }] },
  booking: { label: 'Booking', category: 'marketing', variables: [] },
  newsletter: { label: 'Newsletter', category: 'marketing', variables: [{ name: 'first_name', description: 'Subscriber first name' }] },
  promotion: { label: 'Promotion', category: 'marketing', variables: [] },
  announcement: { label: 'Announcement', category: 'marketing', variables: [] },
  custom: { label: 'Custom', category: 'marketing', variables: [] },
};

const EmailTemplateForm: React.FC<EmailTemplateFormProps> = ({
  template,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState<EmailTemplate>({
    name: '',
    subject: '',
    template_type: 'custom',
    content_html: '',
    content_text: '',
    is_active: true
  });

  const [activeTab, setActiveTab] = useState('visual');
  const [variablesOpen, setVariablesOpen] = useState(true);

  useEffect(() => {
    if (template) {
      setFormData(template);
    }
  }, [template]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleHtmlChange = (html: string) => {
    setFormData(prev => ({ ...prev, content_html: html }));
  };

  const copyVariable = (varName: string) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    toast({
      title: "Copied!",
      description: `{{${varName}}} copied to clipboard`,
    });
  };

  const currentConfig = templateTypeConfig[formData.template_type] || templateTypeConfig.custom;

  const predefinedTemplates = [
    {
      name: 'Welcome Email',
      subject: 'Welcome to Our Platform!',
      content_html: `<h1>Welcome {{first_name}}!</h1><p>Thank you for joining our platform.</p>`,
      content_text: `Welcome {{first_name}}! Thank you for joining.`
    },
    {
      name: 'Booking Confirmation',
      subject: 'Your Booking is Confirmed - {{date}}',
      content_html: `<h1>Booking Confirmed!</h1><p>Hi {{first_name}}, your booking for {{service_name}} on {{date}} is confirmed.</p>`,
      content_text: `Booking Confirmed! Hi {{first_name}}, your booking is confirmed.`
    },
    {
      name: 'Newsletter',
      subject: 'Our Monthly Newsletter - {{date}}',
      content_html: `<h1>Monthly Newsletter</h1><p>Hi {{first_name}}, here's what's new this month...</p>`,
      content_text: `Monthly Newsletter - Hi {{first_name}}, here's what's new.`
    }
  ];

  const loadPredefinedTemplate = (templateData: any) => {
    setFormData(prev => ({
      ...prev,
      name: templateData.name,
      subject: templateData.subject,
      content_html: templateData.content_html,
      content_text: templateData.content_text
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Template Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Start Templates</CardTitle>
          <CardDescription>Choose from our pre-built templates or start from scratch</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {predefinedTemplates.map((template, index) => (
              <div
                key={index}
                className="border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => loadPredefinedTemplate(template)}
              >
                <h4 className="font-medium">{template.name}</h4>
                <p className="text-sm text-gray-600 mt-1">{template.subject}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Template Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter template name"
                required
              />
            </div>
            <div>
              <Label htmlFor="type">Template Type</Label>
              <Select 
                value={formData.template_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, template_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <optgroup label="System Templates">
                    {Object.entries(templateTypeConfig)
                      .filter(([_, config]) => config.category === 'system')
                      .map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                  </optgroup>
                  <optgroup label="Marketing Templates">
                    {Object.entries(templateTypeConfig)
                      .filter(([_, config]) => config.category === 'marketing')
                      .map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                  </optgroup>
                </SelectContent>
              </Select>
              {currentConfig.category === 'system' && (
                <p className="text-xs text-muted-foreground mt-1">
                  System template - used automatically by the platform
                </p>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="subject">Subject Line</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Enter email subject (supports {{variables}})"
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* Variable Helper */}
      {currentConfig.variables.length > 0 && (
        <Collapsible open={variablesOpen} onOpenChange={setVariablesOpen}>
          <Card>
            <CardHeader className="py-3">
              <CollapsibleTrigger className="flex items-center justify-between w-full">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Info className="h-4 w-4" />
                  Available Variables
                </CardTitle>
                <Badge variant="secondary">{currentConfig.variables.length}</Badge>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground mb-3">
                  Click to copy. Use <code className="bg-muted px-1 rounded">{`{{variable_name}}`}</code> in your template.
                  For conditionals: <code className="bg-muted px-1 rounded">{`{{#if variable}}...{{/if}}`}</code>
                </p>
                <div className="flex flex-wrap gap-2">
                  {currentConfig.variables.map((v) => (
                    <Button
                      key={v.name}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="font-mono text-xs"
                      onClick={() => copyVariable(v.name)}
                      title={v.description}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      {`{{${v.name}}}`}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Content Editor */}
      <Card>
        <CardHeader>
          <CardTitle>Email Content</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="visual">{t('dashboard:reportsManager.visualEditor')}</TabsTrigger>
              <TabsTrigger value="html">{t('dashboard:reportsManager.htmlCode')}</TabsTrigger>
              <TabsTrigger value="text">{t('dashboard:reportsManager.plainText')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="visual" className="mt-4">
              <RichTextEditor
                value={formData.content_html}
                onChange={handleHtmlChange}
                placeholder="Create your email content using the visual editor..."
              />
            </TabsContent>
            
            <TabsContent value="html" className="mt-4">
              <div className="space-y-2">
                <Label htmlFor="content_html">HTML Content</Label>
                <Textarea
                  id="content_html"
                  value={formData.content_html}
                  onChange={(e) => setFormData(prev => ({ ...prev, content_html: e.target.value }))}
                  placeholder="Enter HTML content"
                  rows={15}
                  className="font-mono text-sm"
                />
              </div>
            </TabsContent>
            
            <TabsContent value="text" className="mt-4">
              <div className="space-y-2">
                <Label htmlFor="content_text">Plain Text Content</Label>
                <Textarea
                  id="content_text"
                  value={formData.content_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, content_text: e.target.value }))}
                  placeholder="Enter plain text content (optional - will be auto-generated if empty)"
                  rows={10}
                />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg p-4 bg-white">
            <div className="border-b pb-2 mb-4">
              <div className="text-sm text-gray-600">Subject: {formData.subject}</div>
            </div>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={createSafeHTML(formData.content_html)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          <Save className="h-4 w-4 mr-2" />
          {isLoading ? 'Saving...' : 'Save Template'}
        </Button>
      </div>
    </form>
  );
};

export default EmailTemplateForm;